<template>
  <div>
    <b-card
      title="Historial de Versiones"
      no-body
    >
      <b-card-body>
        <b-table
          :items="prompts"
          :fields="fields"
          :busy="loading"
          small
          hover
          responsive
          show-empty
          empty-text="No hay versiones registradas"
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle" />
            </div>
          </template>

          <template #cell(version)="data">
            <b-badge
              :variant="data.item.is_active ? 'light-success' : 'light-secondary'"
            >
              v{{ data.item.version }}
            </b-badge>
          </template>

          <template #cell(is_active)="data">
            <b-badge
              :variant="data.item.is_active ? 'success' : 'secondary'"
            >
              {{ data.item.is_active ? 'Activa' : 'Inactiva' }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <b-button
              v-if="!data.item.is_active"
              size="sm"
              variant="outline-primary"
              :disabled="activating === data.item.id"
              @click="activateVersion(data.item.id)"
            >
              <b-spinner
                v-if="activating === data.item.id"
                small
              />
              <span v-else>Activar</span>
            </b-button>
            <span
              v-else
              class="text-muted small"
            >En uso</span>
          </template>
        </b-table>
      </b-card-body>
    </b-card>

    <b-card title="Editar Prompt Activo">
      <b-card-body>
        <p class="text-muted small mb-1">
          Edita el prompt del sistema que recibe el asistente. Usa <code>{user_name}</code> y <code>{user_role}</code> como variables. Al guardar se crea una nueva versión y el caché se invalida automáticamente.
        </p>

        <b-form-group
          label="Notas de la versión (opcional):"
          label-for="input-notes"
        >
          <b-form-input
            id="input-notes"
            v-model="newNotes"
            placeholder="Ej: Ajuste de tono, corrección de instrucción de totales..."
            maxlength="255"
          />
        </b-form-group>

        <b-form-group
          label="Contenido del prompt:"
          label-for="textarea-prompt"
        >
          <b-form-textarea
            id="textarea-prompt"
            v-model="editContent"
            rows="18"
            max-rows="40"
            style="font-family: monospace; font-size: 0.85rem;"
            placeholder="Escribe el prompt aquí..."
          />
          <small class="text-muted">{{ editContent.length }} caracteres</small>
        </b-form-group>

        <div class="d-flex align-items-center gap-1">
          <b-button
            variant="primary"
            :disabled="saving || editContent.trim().length < 50"
            @click="saveNewVersion"
          >
            <b-spinner
              v-if="saving"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="SaveIcon"
              size="14"
              class="mr-50"
            />
            Guardar como nueva versión
          </b-button>
          <b-button
            variant="flat-secondary"
            :disabled="saving"
            @click="resetToActive"
          >
            Restaurar versión activa
          </b-button>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import axios from '@axios'
import {
  BCard,
  BCardBody,
  BTable,
  BBadge,
  BButton,
  BSpinner,
  BFormGroup,
  BFormInput,
  BFormTextarea,
} from 'bootstrap-vue'

export default {
  name: 'ChatPromptAdmin',
  components: {
    BCard,
    BCardBody,
    BTable,
    BBadge,
    BButton,
    BSpinner,
    BFormGroup,
    BFormInput,
    BFormTextarea,
  },

  data() {
    return {
      prompts: [],
      loading: false,
      saving: false,
      activating: null,
      editContent: '',
      newNotes: '',
      fields: [
        { key: 'version', label: 'Versión', thStyle: { width: '90px' } },
        { key: 'notes', label: 'Notas' },
        { key: 'created_by', label: 'Creado por' },
        { key: 'created_at', label: 'Fecha' },
        { key: 'is_active', label: 'Estado', thStyle: { width: '90px' } },
        { key: 'actions', label: 'Acciones', thStyle: { width: '110px' } },
      ],
    }
  },

  created() {
    this.loadPrompts()
  },

  methods: {
    async loadPrompts() {
      this.loading = true
      try {
        const { data } = await axios.get('system/chat-prompt')
        if (data.res) {
          this.prompts = data.data
          const active = this.prompts.find(p => p.is_active)
          if (active) {
            this.editContent = active.content
          }
        }
      } catch (e) {
        this.showErrors(e)
      } finally {
        this.loading = false
      }
    },

    async saveNewVersion() {
      if (this.editContent.trim().length < 50) return
      this.saving = true
      try {
        const { data } = await axios.post('system/chat-prompt', {
          content: this.editContent,
          notes: this.newNotes || null,
        })
        if (data.res) {
          this.makeToast('success', 'Prompt guardado', data.msg)
          this.newNotes = ''
          await this.loadPrompts()
        }
      } catch (e) {
        this.showErrors(e)
      } finally {
        this.saving = false
      }
    },

    async activateVersion(id) {
      this.activating = id
      try {
        const { data } = await axios.put(`system/chat-prompt/${id}/activate`)
        if (data.res) {
          this.makeToast('success', 'Versión activada', data.msg)
          await this.loadPrompts()
        }
      } catch (e) {
        this.showErrors(e)
      } finally {
        this.activating = null
      }
    },

    resetToActive() {
      const active = this.prompts.find(p => p.is_active)
      if (active) {
        this.editContent = active.content
        this.newNotes = ''
      }
    },
  },
}
</script>
